import React, { useState, useRef, useEffect } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { FaPaperPlane, FaPaperclip, FaRegClipboard, FaTimes, FaBug } from 'react-icons/fa';
import AIBuilderMessage from './AIBuilderMessage';

// Sample-file caps mirror the server's hard limits (3 files / 8000 chars each). We only
// read the first slice of each file and keep the first ~50 lines — enough to infer a
// header row, columns/order, delimiter, or an API object's field names.
const MAX_SAMPLE_FILES = 3;
const MAX_SAMPLE_CHARS = 8000;
const MAX_SAMPLE_LINES = 50;
const HEAD_BYTES = 64 * 1024; // read at most 64KB off the front — never the whole file
const SAMPLE_ACCEPT = '.csv,.txt,.json,.xml,text/csv,text/plain,application/json,text/xml,application/xml';

function clampSampleContent(text) {
  return text
    .split(/\r?\n/)
    .slice(0, MAX_SAMPLE_LINES)
    .join('\n')
    .slice(0, MAX_SAMPLE_CHARS);
}

async function readSampleFile(file) {
  const head = file.slice(0, HEAD_BYTES);
  const text = await head.text();
  return { name: file.name, content: clampSampleContent(text) };
}

function AIBuilderChatPanel({ messages, inFlight, onSend, allowSampleFiles = false, onReportIssue, reportDisabled = false }) {
  const [input, setInput] = useState('');
  const [samples, setSamples] = useState([]); // [{ name, content }]
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [sampleNote, setSampleNote] = useState('');
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, inFlight]);

  const remainingSlots = MAX_SAMPLE_FILES - samples.length;

  const handleFilesSelected = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking the same file later
    if (picked.length === 0) return;

    const accepted = picked.slice(0, remainingSlots);
    if (picked.length > accepted.length) {
      setSampleNote(`Only ${MAX_SAMPLE_FILES} sample files allowed.`);
    } else {
      setSampleNote('');
    }
    try {
      const read = await Promise.all(accepted.map(readSampleFile));
      setSamples((prev) => [...prev, ...read].slice(0, MAX_SAMPLE_FILES));
    } catch {
      setSampleNote('Could not read that file.');
    }
  };

  const addPastedSample = () => {
    const trimmed = pasteText.trim();
    if (!trimmed) return;
    if (samples.length >= MAX_SAMPLE_FILES) {
      setSampleNote(`Only ${MAX_SAMPLE_FILES} sample files allowed.`);
      return;
    }
    setSamples((prev) => [
      ...prev,
      { name: `pasted-${prev.length + 1}.txt`, content: clampSampleContent(trimmed) },
    ]);
    setPasteText('');
    setShowPaste(false);
    setSampleNote('');
  };

  const removeSample = (idx) => {
    setSamples((prev) => prev.filter((_, i) => i !== idx));
    setSampleNote('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || inFlight) return;
    // Second arg is the sample payload; the classic builder's onSend ignores it.
    onSend(trimmed, samples.length > 0 ? samples : undefined);
    setInput('');
    setSamples([]);
    setPasteText('');
    setShowPaste(false);
    setSampleNote('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="ai-chat-panel">
      <div className="ai-chat-scroll" ref={scrollRef}>
        {messages.length === 0 && !inFlight && (
          <div className="ai-chat-empty text-muted small">
            Describe what you want to build.
          </div>
        )}
        {messages.map((m, i) => (
          <AIBuilderMessage key={i} role={m.role} kind={m.kind} content={m.content} />
        ))}
        {inFlight && (
          <div className="ai-bubble-row ai-bubble-row-assistant">
            <div className="ai-bubble ai-bubble-assistant ai-bubble-typing">
              <Spinner animation="grow" size="sm" /> <Spinner animation="grow" size="sm" /> <Spinner animation="grow" size="sm" />
            </div>
          </div>
        )}
      </div>
      <Form onSubmit={handleSubmit} className="ai-chat-input-form">
        {allowSampleFiles && (
          <div className="ai-sample-area">
            {samples.length > 0 && (
              <div className="ai-sample-chips">
                {samples.map((s, i) => (
                  <span key={i} className="ai-sample-chip" title={`${s.content.length} chars`}>
                    <FaPaperclip className="me-1" size={10} />
                    <span className="ai-sample-chip-name">{s.name}</span>
                    <button
                      type="button"
                      className="ai-sample-chip-remove"
                      onClick={() => removeSample(i)}
                      aria-label={`Remove ${s.name}`}
                    >
                      <FaTimes size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {showPaste && (
              <div className="ai-paste-box">
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder="Paste a sample (CSV rows, JSON, XML)…"
                  className="ai-chat-input"
                />
                <div className="d-flex justify-content-end mt-1">
                  <Button variant="link" size="sm" className="p-0 me-3 text-muted" onClick={() => { setShowPaste(false); setPasteText(''); }}>
                    Cancel
                  </Button>
                  <Button variant="link" size="sm" className="p-0" onClick={addPastedSample} disabled={!pasteText.trim()}>
                    Add sample
                  </Button>
                </div>
              </div>
            )}

            <div className="ai-attach-row d-flex align-items-center">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={SAMPLE_ACCEPT}
                onChange={handleFilesSelected}
                style={{ display: 'none' }}
              />
              <Button
                variant="link"
                size="sm"
                className="ai-attach-btn p-0 me-3 text-muted"
                onClick={() => fileInputRef.current?.click()}
                disabled={samples.length >= MAX_SAMPLE_FILES}
                title="Attach a sample file (CSV, txt, JSON, XML)"
              >
                <FaPaperclip className="me-1" size={11} /> Attach sample
              </Button>
              <Button
                variant="link"
                size="sm"
                className="ai-attach-btn p-0 me-3 text-muted"
                onClick={() => setShowPaste((v) => !v)}
                disabled={samples.length >= MAX_SAMPLE_FILES}
                title="Paste a sample"
              >
                <FaRegClipboard className="me-1" size={11} /> Paste
              </Button>
              {onReportIssue && (
                <Button
                  variant="link"
                  size="sm"
                  className="ai-attach-btn p-0 text-muted"
                  onClick={onReportIssue}
                  disabled={reportDisabled}
                  title="Report an issue — download this conversation for support"
                >
                  <FaBug className="me-1" size={11} /> Report issue
                </Button>
              )}
              {sampleNote && <span className="ai-sample-note text-muted ms-2">{sampleNote}</span>}
            </div>
          </div>
        )}
        <div className="d-flex">
          <Form.Control
            as="textarea"
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe what you want to build..."
            disabled={inFlight}
            className="ai-chat-input"
          />
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={!input.trim() || inFlight}
            className="ai-chat-send-btn ms-2"
          >
            <FaPaperPlane />
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default AIBuilderChatPanel;
